import React, { useEffect, useRef } from 'react';
import { PulseLoader } from 'react-spinners';
import './ChatBotWIndow.css';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import IsMobile from '../Tools/IsMobileDevice';

const InputValidator = (input) => {
  return input.length > 0 && input.trim().length != 0;
};

export default function ChatBoxWindow({ onClose }) {
  const inputRef = React.createRef();
  const chatContainer = React.createRef();

  const [chat, setChat] = React.useState([]);
  const [disabled, setDisabled] = React.useState(false);
  const [issue, setIssue] = React.useState(null);
  const [isLarge, setIsLarge] = React.useState(false);

  const isMobile = IsMobile();

  const WS = useRef(null);

  let runOnce;

  /**
   * Toggle the size of the chat window
   */
  const ToggleSize = () => {
    setIsLarge((prev) => !prev);
  };

  const ClearChat = () => {
    if (!disabled) {
      setChat([]);

      AddBotMessage('Hello! Ask me anything about Sokn Engineering!');
    }
  };

  const AddErrorMessage = (text) => {
    setChat((prevArray) => [...prevArray, { text: text, from: 'error' }]);
  };

  /**
   * Add a bot message to the chat window
   * @param {String} text
   */
  const AddBotMessage = (text) => {
    setChat((prevArray) => [...prevArray, { text: text, from: 'bot' }]);
  };

  /**
   * Print out the client text to the chat window
   * @param {String} text
   */
  const AddClientMessage = (text) => {
    inputRef.current.value = '';

    if (InputValidator(text)) {
      setChat((prevArray) => [...prevArray, { text: text, from: 'user' }]);
      AddBotMessage(''); //A placeholder message for the application to override while streaming in
      setDisabled(true); //Disable the chat so users can't spam

      WS.current.send(
        JSON.stringify({
          publicKey: 'Iu*dq@*LAhOMy%*#4Yz24mDuwiy@&qIlrxoqO6YHckamt0p!2^1Kow!4cSRqmXzRNIAewCBZc7BwlDjK33dzLOeK1RFFeaRP6%p',
          message: text,
        })
      );
    }
  };

  /**
   * Update the last message in the chat array.
   * @param {String} text
   */
  const UpdateLastBotMessage = (text) => {
    setChat((prev) => [...prev.slice(0, -1), { ...prev.at(-1), text: prev.at(-1).text + text }]);
  };

  useEffect(() => {
    if (!runOnce) {
      runOnce = true;

      WS.current = new WebSocket('wss://chatbot-worker.sokn-engineering.workers.dev/ws');
      WS.current.onopen = () => {
        console.log('WebSocket connection opened');
        setIssue(null);

      };
      WS.current.onmessage = (event) => {
        const payload = JSON.parse(event.data);

        //Check for the end
        if (payload?.status === 1) {
          setDisabled(false);
          return;
        }

        UpdateLastBotMessage(payload);
      };
      WS.current.onclose = () => {
        console.log('WebSocket connection closed');

        setIssue('');
      };

      AddBotMessage('Hello! Ask me anything about Sokn Engineering!');
    }
  }, []);

  useEffect(() => {
    //Scroll to the last element
    const lastMessage = chatContainer.current.lastElementChild;
    if (lastMessage) lastMessage.scrollIntoView({ behavior: 'auto', block: 'end' });
  }, [chat]);

  return (
    <>
      <div
        id='ChatBotWindow'
        style={{
          right: isMobile ? '0px' : '40px',
          height: isMobile ? '80vh' : isLarge ? '700px' : '600px',
          width: isMobile ? '100%' : isLarge ? '800px' : '400px',
        }}>
        <div className='chatBotWindowHeader'>
          <p style={{ color: 'white', fontWeight: 'bold', fontFamily: 'Jura' }}>Lagertha</p>

          <div style={{ display: 'flex', gap: '5px' }}>
            <div className={disabled ? 'chatBotExitButton-disabled' : 'chatBotExitButton'} onClick={() => ClearChat()}>
              <img alt='' height={20} width={20} src='/Icons/Trash.svg' />
            </div>

            {!isMobile && (
              <div className='chatBotExitButton' onClick={() => ToggleSize()}>
                <img alt='' src={isLarge ? '/Icons/ExpandIn.svg' : '/Icons/ExpandOut.svg'} />
              </div>
            )}

            <div className='chatBotExitButton' onClick={() => onClose()}>
              <img alt='' src='/Icons/Close.svg' />
            </div>
          </div>
        </div>

        <div ref={chatContainer} id='chatBotMessages'>
          {chat.map((message, index) => {
            return (
              <React.Fragment key={index}>
                {(() => {
                  switch (message.from) {
                    case 'bot':
                      return (
                        <div key={index} className='chatBotBotMessage'>
                          <>
                            <div className='chatBotBotIcon'>
                              <img alt='' src='/Images/BotIcon.png' height={30} width={30} />
                            </div>
                          </>

                          <div className='chatBotBotMessageText'>
                            <p style={{ color: 'white' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(message.text)) }}></p>
                          </div>
                        </div>
                      );

                    case 'user':
                      return (
                        <div key={index} className='chatBotUserMessage'>
                          <div className='chatBotUserMessageText'>
                            <p style={{ color: 'white' }}>{message.text}</p>
                          </div>

                          <div className='chatBotUserIcon'>
                            <img alt='' src='/Icons/Person.svg' height={24} width={24} />
                          </div>
                        </div>
                      );

                    case 'error':
                      return (
                        <div key={index} style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <p style={{ color: 'black', fontWeight: 'bold', fontSize: '12px' }}>{message.text}</p>
                        </div>
                      );

                    default:
                      break;
                  }
                })()}
              </React.Fragment>
            );
          })}
        </div>

        <div className='chatBotInput'>
          <input
            onKeyUp={(e) => {
              if (e.code === 'Enter' && !disabled) {
                AddClientMessage(inputRef.current.value);
              }
            }}
            ref={inputRef}
            className='chatBotInputMessage'
            placeholder='Send a message...'></input>

          <div
            className={disabled ? 'chatBotInputSend-disabled' : issue ? 'chatBotInputSend-issue' : 'chatBotInputSend'}
            onClick={() => {
              if (!disabled) AddClientMessage(inputRef.current.value);
            }}>
            {disabled ? (
              <>
                <PulseLoader size={10} color='white' />
              </>
            ) : (
              <>
                {issue ? (
                  <>
                    <img alt='' src={'/Icons/LostConnection.svg'} />
                  </>
                ) : (
                  <>
                    <img alt='' src={'/Icons/Send.svg'} />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
